<template>
    <div>
        <div
            class="mt-input mt-input--growable"
            :class="{ disabled, active, filled, invalid }"
            @click="$emit('click', $event)"
        >
            <div v-if="coins" class="coins">{{ coins }}</div>
            <div
                v-if="label && !isCalendar"
                class="label"
                :class="{ 'hold-place': !filled, invalid }"
            >
                {{ label }}<span class="required" v-if="required">&nbsp;&nbsp;*</span>
            </div>
            <div
                v-else
                class="model-string-label"
                :class="{ 'model-string-label--hold': !filled, invalid }"
            >
                <CalendarIcon
                    v-if="isCalendar"
                    class="calendar-icon"
                    :class="{ 'calendar-icon--hidden': filled }"
                />
                <span :class="{ 'model-string-label--fixed': filled }">{{ label }}</span>
            </div>

            <textarea
                v-if="inputComponent === 'input' && isMultipleLines"
                class="multiple"
                :class="{ disabled, invalid }"
                :value="innerValue"
                @input="inputHandler"
                @change="changeHandler"
                ref="field"
                :disabled="disabled"
                :readonly="readonly"
                @focus="active = true"
                @blur="active = false"
                @wheel="preventValueWheelScroll"
                :maxlength="maxlength"
            ></textarea>
            <input
                v-else-if="inputComponent === 'input'"
                class="mt-input__value"
                :type="type"
                :class="{ disabled }"
                :value="innerValue"
                @input="inputHandler"
                @change="changeHandler"
                ref="field"
                :disabled="disabled"
                :readonly="readonly"
                @focus="active = true"
                @blur="active = false"
                @wheel="preventValueWheelScroll"
                :maxlength="maxlength"
            />
            <component
                v-else
                :is="inputComponent"
                class="mt-input__value"
                :class="{ disabled, invalid }"
                :value="innerValue"
                @input="inputValueHandler"
                :disabled="disabled"
                :readonly="readonly"
                @focus="active = true"
                @blur="active = false"
                :maxlength="maxlength"
            />

            <div class="mt-input-counter" v-if="maxlength && !hiddenLenghtCounter">
                {{ currentLength }} / {{ maxlength }}
            </div>
        </div>
        <div v-if="errorMessage" class="mt-invalid">
            <div class="mt-invalid-text">
                {{ errorMessage }}
            </div>
        </div>
    </div>
</template>

<script>
import Inputmask from 'inputmask';
import TelInput from '../inputs/TelInput';
import CalendarIcon from '@/assets/images/icons/input-calendar.svg?inline';

export default {
    name: 'ModelString',
    components: {
        TelInput,
        CalendarIcon,
    },
    props: {
        coins: Number,
        label: String,
        value: [String, Object, Array, Number],
        type: {
            type: String,
            default: 'text',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disabledValue: {
            type: Boolean,
            default: false,
        },
        isMultipleLines: {
          type: Boolean,
          default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        valid: {
            type: Boolean,
            default: true,
        },
        mask: {
            type: String,
            default: null,
        },
        maskPlaceholder: {
            type: String,
            default: '_',
        },
        inputFilter: {
            type: Function,
            default: null,
        },
        invalidateFunction: {
            type: Function,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        maxlength: {
            type: String,
            default: '',
        },
        isCalendar: {
            type: Boolean,
            default: false,
        },
        hiddenLenghtCounter: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            innerValue: this.value ? this.value : '',
            active: false,
        };
    },
    model: {
        prop: 'value',
        event: 'change',
    },
    watch: {
        value(str) {
            this.innerValue = str;
        },
        innerValue(str) {
            this.$emit('change', str);
            this.$emit('error', this.invalidateFunction
                ? !!this.invalidateFunction(this.innerValue)
                : false);
        },
        mask() {
            this.applyMask();
        },
    },
    mounted() {
        if(this.isMultipleLines) {
          this.$refs.field.style.height = `${this.$refs.field.scrollHeight}px`;

        }
        this.applyMask();
    },
    computed: {
        inputComponent() {
            if (this.type === 'tel') return 'TelInput';

            return 'input';
        },

        filled() {
            return this.active || this.value;
        },

        invalid() {
            return (
                !this.valid ||
                (this.invalidateFunction && this.invalidateFunction(this.value))
            );
        },

        errorMessage() {
            return this.invalidateFunction
                ? this.invalidateFunction(this.value)
                : '';
        },

        currentLength() {
            return this.innerValue.length;
        },
    },
    methods: {
        inputValueHandler(value) {
            this.innerValue = value;
        },
        inputHandler(evt) {
            if(this.isMultipleLines) {
              evt.target.style.height = '44px';
              evt.target.style.height = `${evt.target.scrollHeight}px`;
            }
            let value = evt.target.value;
            const input = this.$refs.field;
            if (this.inputFilter) {
                if (this.inputFilter(value)) {
                    this.innerValue = value;
                } else {
                    input.value = this.innerValue || '';
                }
            } else {
                this.innerValue = value;
            }
        },
        changeHandler(evt) {
            const value = evt.target.value;
            if (this.maxlength) {
                if(value.length > Number(this.maxlength)){
                    this.innerValue = this.value.slice(0, Number(this.maxlength));
                }
            }
        },
        applyMask() {
            if (this.inputComponent !== 'input') return;
            Inputmask.remove(this.$refs.field);
            if (this.mask) {
                let m = new Inputmask(this.mask, {
                    placeholder: this.maskPlaceholder,
                });
                m.mask(this.$refs.field);
            }
        },
        preventValueWheelScroll(e) {
            if (e instanceof Event) {
                e.preventDefault();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../styles/mt';

.calendar-icon {
    display: block;
    width: mrem(32);
    z-index: 9;
    @include on-desktop {
        width: rem(28);
    }

    &--hidden {
        display: none;
    }
}
.model-string-label {
    display: flex;
    align-items: center;
    color: #98a0a9;
    &--calendar {
        padding-left: rem(34) !important;
    }

    &--hold {
        top: mrem(12);
        font-size: mrem(16);
        line-height: mrem(24);

        @include on-desktop {
            top: rem(12);
            font-size: rem(16);
            line-height: rem(24);
            width: calc(100% - 3rem);
            display: flex;
            justify-content: flex-start;
            align-items: center;
        }
    }

    &--fixed {
        position: absolute;
        color: #4663AB;
        background: #fff;
        padding: 0 mrem(8);
        font-weight: 600;
        top: mrem(-8);
        left: mrem(7);

        @include on-desktop {
            padding: 0 rem(8);
            top: rem(-8);
            left: rem(7);
            font-size: rem(12);
            font-weight: 600;
        }
    }
}

.multiple {
  resize: none;
  height: mrem(44);
  font-size: mrem(16);
  font-family: 'Proxima Nova', sans-serif;

  @include on-desktop {
    height: rem(44);
    font-size: rem(16);
  }
}

.mt-invalid-text {
    @include set-font-size(12, 500, 18);
}

.mt-input-counter {
    position: absolute;
    bottom: urem(-8);
    right: urem(7);
    background: #fff;
    padding: 0 urem(8);
    color: #98a0a9;

}
</style>
